import classNames from 'classnames';
import { useEffect, useRef } from 'react';
import LazyLoad from 'vanilla-lazyload';

const isServer = typeof window === 'undefined';

// Only initialize lazyload one time for the entire application
if (!isServer && !window.lazyLoadInstance) {
  window.lazyLoadInstance = new LazyLoad({
    elements_selector: '.lazy', // CSS selector of the elements to load lazily
    load_delay: 1, // Time (in ms) each img needs to stay inside the viewport before its loading begins
  });
}

type Props = {
  alt: string;
  src?: string | null;
  classnames?: string;
  imageType?: 'background' | 'picture';
  backgroundImageSrc?: string;
  mobileImgSrc?: string;
}

export default function LazyImage({
  alt,
  src,
  classnames,
  imageType,
  backgroundImageSrc,
  mobileImgSrc,
}: Props) {
  const imageRef = useRef(null);
  useEffect(() => {
    window.lazyLoadInstance.update();
    if (imageRef.current) {
      window.lazyLoadInstance.load(imageRef.current, true);
    }
  }, [src, backgroundImageSrc, mobileImgSrc]);

  const classes = classNames({
    'lazy': true,
    [classnames as string]: !!classnames,
  });

  switch (imageType) {
    case 'background':
      // Render the background image with data-bg
      return <div className={classes} data-bg={`url(${backgroundImageSrc})`} ref={imageRef} />;
    case 'picture':
      // Render the picture element with data-srcset for the source element
      return (
        <picture ref={imageRef}>
          <source data-srcset={mobileImgSrc} media="(max-width: 767px)" />
          <img alt={alt} className={classes} data-src={src} />
        </picture>
      );
    default:
      // Render the image with data-src
      return <img alt={alt} className={classes} data-src={src} ref={imageRef} />;
  }
}
